import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import {
  ChevronDown,
  Sparkles,
  Search,
  MoreVertical,
  Trash2,
  Check,
} from "lucide-react";
import copyIcon from "../../../Asset/copy-icon.svg";
import deleteIcon from "../../../Asset/delete-icon.svg";
import promptsIcon from "../../../Asset/Prompts.svg";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const formatTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${String(minutes).padStart(2, "0")} ${meridiem}`;
};

const formatRelativeDate = (date, now = new Date()) => {
  const diffDays = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / 86400000
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  const sameYear = date.getFullYear() === now.getFullYear();
  const month = MONTHS_SHORT[date.getMonth()];
  return sameYear
    ? `${month} ${date.getDate()}`
    : `${month} ${date.getDate()}, ${date.getFullYear()}`;
};

const formatDateTime = (value) => {
  if (!value) return { date: "", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "", time: "" };
  return { date: formatRelativeDate(date), time: formatTime(date) };
};

/* ------------------------------------------------------------------ */
/* Confirmation modal (shared by "Delete All" and "Delete Selected")  */
/* ------------------------------------------------------------------ */
const ConfirmDeleteModal = ({ open, title, message, confirmLabel, onCancel, onConfirm }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#0000007a] backdrop-blur-[2px] flex items-center justify-center p-4 font-inter"
      style={{ animation: "modalBackdropIn 200ms ease-out both" }}
      onClick={onCancel}
    >
      <style>{`
        @keyframes modalBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      <div
        className="bg-white rounded-[24px] border border-[#E5E7EB] w-full max-w-[420px] p-6 sm:p-7 shadow-2xl text-center"
        style={{ animation: "modalIn 300ms cubic-bezier(0.16, 1, 0.3, 1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-[#0F1115] leading-tight">{title}</h2>
        <p className="mt-3 text-sm text-[#5C6370] leading-relaxed">{message}</p>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 bg-white border border-[#E5E7EB] text-[#5C6370] hover:bg-[#F2F3F6] rounded-xl font-semibold transition duration-200 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-3 rounded-xl font-semibold transition duration-200 text-sm hover:opacity-90"
            style={{ background: "#FFE8E8", color: "#FB2C36" }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const TranscriptHistoryPanel = ({
  items,
  promptOptions,
  onApplyPrompt,
  onDelete,
  onDeleteMany,
  onDeleteAll,
  onCopy,
}) => {
  const [expandedIds, setExpandedIds] = useState({});
  const [openPromptMenuById, setOpenPromptMenuById] = useState({});
  const [applyingById, setApplyingById] = useState({});
  const menuRefs = useRef({});

  /* --- header / bulk-delete state --- */
  const [search, setSearch] = useState("");
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmType, setConfirmType] = useState(null); // "all" | "selected" | null
  const actionMenuRef = useRef(null);
  const panelRef = useRef(null);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /* close the per-card prompt menus on outside click */
  useEffect(() => {
    const onDocumentClick = (event) => {
      setOpenPromptMenuById((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.keys(next).forEach((id) => {
          if (!next[id]) return;
          const host = menuRefs.current[id];
          if (host && !host.contains(event.target)) {
            next[id] = false;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  /* close the 3-dot action menu on outside click */
  useEffect(() => {
    if (!actionMenuOpen) return;
    const onDocumentClick = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [actionMenuOpen]);

  /* cancel selection mode entirely when the user clicks anywhere outside the panel */
  useEffect(() => {
    if (!selectionMode || selectedIds.size === 0) return;
    const onDocumentClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setSelectionMode(false);
        setSelectedIds(new Set());
        setActionMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [selectionMode, selectedIds]);

  const handleApply = async (id, prompt) => {
    if (!prompt) return;
    setApplyingById((prev) => ({ ...prev, [id]: true }));
    setOpenPromptMenuById((prev) => ({ ...prev, [id]: false }));
    try {
      await onApplyPrompt?.(id, prompt);
    } finally {
      setApplyingById((prev) => ({ ...prev, [id]: false }));
    }
  };

  /* --- selection helpers --- */
  const enterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedIds(new Set());
    setActionMenuOpen(false);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setActionMenuOpen(false);
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* --- filtered list based on search --- */
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((item) => {
      const title = String(item.title || "").toLowerCase();
      const content = String(item.content || "").toLowerCase();
      return title.includes(q) || content.includes(q);
    });
  }, [items, search]);

  /* --- confirm actions --- */
  const confirmDeleteAll = () => {
    onDeleteAll?.();
    setConfirmType(null);
    exitSelectionMode();
  };

  const confirmDeleteSelected = () => {
    onDeleteMany?.(Array.from(selectedIds));
    setConfirmType(null);
    exitSelectionMode();
  };

  if (!items?.length) return null;

  const selectedCount = selectedIds.size;

  return (
    <div ref={panelRef} className="rounded-3xl border border-[#D6ECFB] bg-white p-4">
      {/* ---------- Header ---------- */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-4 py-1.5 text-white text-lg font-semibold">
          <span>History</span>
          <span className="inline-flex items-center justify-center rounded-full bg-white/20 text-xs px-2 py-0.5">
            {items.length}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          {/* Delete button — only in selection mode with a selection */}
          {selectionMode && selectedCount > 0 && (
            <button
              type="button"
              onClick={() => setConfirmType("selected")}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
              style={{ background: "#FFE8E8", color: "#FB2C36" }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {/* mobile: icon + count only; sm+ : full label */}
              <span className="sm:hidden">{selectedCount}</span>
              <span className="hidden sm:inline">Delete ({selectedCount})</span>
            </button>
          )}

          {/* Search */}
          <div className="relative shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-32 sm:w-56 rounded-full bg-[#F3F4F6] border border-transparent focus:border-[#3AB1F6] focus:bg-white pl-9 pr-3 py-1.5 text-sm text-[#374151] placeholder:text-[#9CA3AF] outline-none transition"
            />
          </div>

          {/* 3-dot action menu */}
          <div className="relative" ref={actionMenuRef}>
            <button
              type="button"
              onClick={() => setActionMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[#4B5563] hover:bg-[#F3F4F6] transition"
              aria-label="History options"
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            {actionMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-40 w-40 rounded-xl border border-[#E5E7EB] bg-white shadow-lg py-1">
                {selectionMode ? (
                  <button
                    type="button"
                    onClick={exitSelectionMode}
                    className="w-full text-left px-4 py-2 text-sm text-[#374151] hover:bg-[#F3F4F6] transition"
                  >
                    Cancel Selection
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={enterSelectionMode}
                    className="w-full text-left px-4 py-2 text-sm text-[#374151] hover:bg-[#F3F4F6] transition"
                  >
                    Select
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setActionMenuOpen(false);
                    setConfirmType("all");
                  }}
                  className="w-full text-left px-4 py-2 text-sm font-medium transition hover:bg-[#FFF5F5]"
                  style={{ color: "#FB2C36" }}
                >
                  Delete All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Cards ---------- */}
      {filteredItems.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#9CA3AF]">
          No history matches “{search.trim()}”.
        </div>
      ) : (
        <div className="flex items-stretch gap-4 overflow-x-auto pb-2 custom-scrollbar">
          {filteredItems.map((item) => {
            const expanded = Boolean(expandedIds[item.id]);
            const words = String(item.content || "")
              .trim()
              .split(/\s+/)
              .filter(Boolean);
            const isLong = words.length > 50;
            const applying = Boolean(applyingById[item.id]);
            const menuOpen = Boolean(openPromptMenuById[item.id]);
            const previewText = isLong ? `${words.slice(0, 50).join(" ")}...` : item.content;

            const { date: dateLabel, time: timeLabel } = formatDateTime(item.createdAt);
            const isAi = Boolean(item.aiProcessed);
            const promptLabel = item.promptLabel || "";
            const promptBg = item.promptBgColor || "#E8F4FF";
            const isSelected = selectedIds.has(item.id);

            return (
              <div
                key={item.id}
                onClick={selectionMode ? () => toggleSelected(item.id) : undefined}
                className={`relative shrink-0 w-[300px] min-h-[320px] rounded-[16px] border border-[rgba(58,177,246,0.10)] bg-[#F8FBFF] p-4 flex flex-col transition ${
                  selectionMode ? "cursor-pointer" : ""
                }`}
              >
                {/* selection checkbox */}
                {selectionMode && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelected(item.id);
                    }}
                    style={{ border: "1px solid #3AB1F6" }}
                    className={`absolute top-3 right-3 z-10 flex items-center justify-center w-6 h-6 rounded-full transition ${
                      isSelected
                        ? "bg-[#3AB1F6] text-white"
                        : "bg-white text-transparent"
                    }`}
                    aria-label={isSelected ? "Deselect" : "Select"}
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </button>
                )}

                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-[#000] font-inter text-[14px] not-italic font-semibold leading-[19.6px] line-clamp-2">
                    {item.title || "Untitled"}
                  </h3>
                  {!selectionMode && (dateLabel || timeLabel) && (
                    <div className="shrink-0 flex flex-col items-end leading-tight">
                      {dateLabel && (
                        <span className="text-[11px] font-semibold text-[#374151]">
                          {dateLabel}
                        </span>
                      )}
                      {timeLabel && (
                        <span className="text-[10px] font-medium text-[#6B7280] tabular-nums">
                          {timeLabel}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {(isAi || promptLabel) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {isAi && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm"
                        style={{
                          background: "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
                        }}
                        title="Processed with AI"
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        AI
                      </span>
                    )}
                    {promptLabel && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-[#1F2937] border border-[rgba(58,177,246,0.18)]"
                        style={{ background: promptBg }}
                        title={`Prompt: ${promptLabel}`}
                      >
                        {item.promptIcon ? (
                          <ReactSVG
                            src={item.promptIcon}
                            beforeInjection={(svg) =>
                              svg.setAttribute("class", "w-2.5 h-2.5")
                            }
                            className="w-2.5 h-2.5"
                          />
                        ) : null}
                        <span className="max-w-[120px] truncate">{promptLabel}</span>
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-3 flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 min-h-0 overflow-y-auto pr-1 custom-scrollbar">
                    <p className="text-[#000] font-inter text-[12px] not-italic font-normal leading-[18.6px] tracking-[-0.12px] whitespace-pre-wrap">
                      {expanded || !isLong ? item.content : previewText}
                    </p>
                  </div>

                  {isLong && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(item.id);
                      }}
                      className="mt-2 text-sm text-[#3AB1F6] font-medium text-left"
                    >
                      {expanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-[#D6ECFB]">
                  <div className="text-xs text-[#4B5563]">
                    {item.wordCount} {item.wordCount === 1 ? "word" : "words"}
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <div
                      className="relative"
                      ref={(node) => {
                        menuRefs.current[item.id] = node;
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenPromptMenuById((prev) => ({
                            ...prev,
                            [item.id]: !prev[item.id],
                          }));
                        }}
                        disabled={applying || !promptOptions?.length || selectionMode}
                        className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] text-white px-2 py-1 text-xs font-medium disabled:opacity-60"
                      >
                        <ReactSVG
                          src={promptsIcon}
                          beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                          className="w-3 h-3"
                        />
                        {applying ? "Applying..." : "Prompts"}
                        <ChevronDown className="size-3" />
                      </button>

                      {menuOpen && (
                        <div className="absolute z-30 bottom-full mb-1 w-48 max-h-[144px] overflow-y-auto rounded-lg border border-[#D6ECFB] bg-[#F1FAFF] shadow-md custom-scrollbar">
                          {promptOptions.map((prompt) => (
                            <button
                              key={prompt.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApply(item.id, prompt);
                              }}
                              style={{ "--bg": prompt?.bgColor }}
                              className="group w-full flex items-center gap-2 text-left px-2.5 py-2 text-xs font-inter text-[#363636] hover:bg-[#3AB1F6] hover:text-white transition"
                            >
                              <span className="p-1.5 rounded-md transition bg-[var(--bg)] group-hover:bg-white">
                                <ReactSVG
                                  src={prompt.icon}
                                  beforeInjection={(svg) =>
                                    svg.setAttribute("class", "w-3 h-3")
                                  }
                                  className="w-3 h-3"
                                />
                              </span>
                              {prompt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy?.(item.content);
                      }}
                      disabled={selectionMode}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#F2FAFF] text-[#2196F3] px-2 py-1 text-xs font-medium disabled:opacity-60"
                    >
                      <ReactSVG
                        src={copyIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                        className="w-3 h-3"
                      />
                      Copy
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(item.id);
                      }}
                      disabled={selectionMode}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#FFF3F3] text-[#FF4D4F] px-2 py-1 text-xs font-medium disabled:opacity-60"
                    >
                      <ReactSVG
                        src={deleteIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                        className="w-3 h-3"
                      />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- Confirmation modals ---------- */}
      <ConfirmDeleteModal
        open={confirmType === "all"}
        title="Delete History?"
        message="This will permanently remove all of your transcription history from this device. This action cannot be undone."
        confirmLabel="Delete History"
        onCancel={() => setConfirmType(null)}
        onConfirm={confirmDeleteAll}
      />

      <ConfirmDeleteModal
        open={confirmType === "selected"}
        title="Delete Selected History?"
        message={`You're about to permanently delete ${selectedCount} selected history ${
          selectedCount === 1 ? "entry" : "entries"
        }. This action cannot be undone.`}
        confirmLabel="Delete History"
        onCancel={() => setConfirmType(null)}
        onConfirm={confirmDeleteSelected}
      />
    </div>
  );
};

export default TranscriptHistoryPanel;
